import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag, ImportWizard, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import {
  useC360List, C360Table, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon, CardIcon,
} from './c360';
import { ProductGroupsPage } from './ProductGroupsPage';
import { ProductSubGroupsPage } from './ProductSubGroupsPage';
import { ProducersPage } from './ProducersPage';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
// #717 "Varsayılan Stok Tipi" UI'dan (kart + liste kolonu/filtre/export) komple söküldü;
// import satır-eşleme (stockTypeFromLabel) geriye-uyum için kalır.
import { stockTypeFromLabel } from './stockTypes';

const API_BASE = '/api/products';

const EMPTY_FORM = {
  textId: '', name: '', shortName: '', description: '',
  erpId: '', status: '', unit: '',
  groupId: '', subGroupId: '', producerId: '',
  productImage: '',
};

// Ürün Kartı (v2) ek kolonları — listede varsayılan kapalı (Kolonlar menüsünde tiksiz görünür).
const DEFAULT_HIDDEN_COLUMNS = {
  trackingCode: false, prefix: false, applicationArea: false, approvalStatus: false,
  productType: false, gtip: false, centerCode: false, specialCode: false, countryCode: false,
  alcoholRate: false, maxDiscountRate: false, maxDiscountAmount: false,
  weight: false, volume: false, liter: false, caseQuantity: false,
  level1ProductId: false, level2ProductId: false, dynamicGroupId: false,
};

// status: 1 = Aktif (statusPill semantiği ile uyumlu), 0 = Pasif, 2 = Beklemede
const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const GROUP_OPTIONS = [
  { value: 'groupId', label: 'Ürün Grubu' },
  { value: 'subGroupId', label: 'Alt Grup' },
  { value: 'producerId', label: 'Üretici' },
  { value: 'status', label: 'Durum' },
];

const GROUP_BY_ODATA = {
  groupId: { field: 'GroupId', type: 'guid' },
  subGroupId: { field: 'SubGroupId', type: 'guid' },
  producerId: { field: 'ProducerId', type: 'guid' },
  status: { field: 'Status', type: 'number' },
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  name: { type: 'text', odataField: 'Name' },
  shortName: { type: 'text', odataField: 'ShortName' },
  unit: { type: 'number', odataField: 'Unit' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  groupLabel: { type: 'select', odataField: 'GroupId', valueFormat: 'guid' },
  subGroupLabel: { type: 'select', odataField: 'SubGroupId', valueFormat: 'guid' },
  producerLabel: { type: 'select', odataField: 'ProducerId', valueFormat: 'guid' },
  // #717 "Varsayılan Stok Tipi" kolon filtresi kaldırıldı (alan komple söküldü).
};

const SEARCH_FIELDS = ['TextId', 'Name', 'ShortName'];

// Liste/Kanban/Grafik + Kart görünümü. Kart, Liste'nin hemen YANINDA (index 1); geri kalan modlar korunur.
const PRODUCT_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const definitionTabs = [
  { key: 'products', label: 'Ürünler' },
  { key: 'groups', label: 'Ürün Grubu' },
  { key: 'subGroups', label: 'Ürün Alt Grubu' },
  { key: 'producers', label: 'Üreticiler' },
];

const productDefinitionStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: {
    minHeight: 36, padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700,
  },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

function toNullableGuid(value) {
  return value ? value : null;
}
function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}
function toRawBase64(value) {
  if (!value) return null;
  const idx = value.indexOf('base64,');
  return idx >= 0 ? value.slice(idx + 'base64,'.length) : value;
}

// Backend ProductImage çıplak base64; data URI Chrome'da ERR_INVALID_URL'e yol açabildiği için Blob URL üret.
function useImageObjectUrl(value) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!value) { setUrl(''); return undefined; }
    if (/^(data:|https?:)/i.test(value)) { setUrl(value); return undefined; }
    let objectUrl = '';
    try {
      let clean = value.replace(/[^A-Za-z0-9+/=]/g, '');
      const pad = clean.length % 4;
      if (pad === 2) clean += '==';
      else if (pad === 3) clean += '=';
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const mime = clean.startsWith('/9j/') ? 'image/jpeg'
        : clean.startsWith('R0lGOD') ? 'image/gif'
        : clean.startsWith('UklGR') ? 'image/webp'
        : 'image/png';
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
      setUrl(objectUrl);
    } catch {
      setUrl('');
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [value]);
  return url;
}

function ProductThumb({ value }) {
  const url = useImageObjectUrl(value);
  if (!url) return '—';
  return <img src={url} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '50%', border: '1px solid var(--border-color, #e5e7eb)' }} />;
}

function ProductImagePreview({ value, name }) {
  const url = useImageObjectUrl(value);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={name || 'Ürün görseli'}
      style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #f9fafb)' }}
    />
  );
}

// === Ürün Grafik görünümü ===
function ChartView({ rows, getGroupLabel, getProducerLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => Number(r.status) === 1).length;
  const withImage = rows.filter((r) => r.productImage).length;
  const groupSet = new Set(rows.map((r) => r.groupId).filter(Boolean));
  const producerSet = new Set(rows.map((r) => r.producerId).filter(Boolean));
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  // Ürün grubuna göre dağılım (ilk 12)
  const groupCounts = {};
  rows.forEach((r) => {
    const lbl = r.groupId ? getGroupLabel(r.groupId) : 'Atanmamış';
    groupCounts[lbl] = (groupCounts[lbl] || 0) + 1;
  });
  const groupData = Object.entries(groupCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 12);

  // Durum donut
  const statusPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const statusCounts = {};
  rows.forEach((r) => {
    const k = r.status == null || r.status === '' ? '__none__' : Number(r.status);
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([k, v]) => {
    const meta = STATUS_META[k];
    return { name: meta?.label || 'Bilinmiyor', value: v, color: meta ? statusPalette[meta.tone] : '#cbd5e1' };
  });

  // Üreticiye göre ilk N donut
  const producerCounts = {};
  rows.forEach((r) => {
    const lbl = r.producerId ? getProducerLabel(r.producerId) : 'Atanmamış';
    producerCounts[lbl] = (producerCounts[lbl] || 0) + 1;
  });
  const producerPalette = ['#0073ea', '#00c875', '#a855f7', '#fdab3d', '#0ea5e9', '#e2445c', '#14b8a6', '#94a3b8'];
  const producerData = Object.entries(producerCounts)
    .map(([name, value], i) => ({ name, value, color: producerPalette[i % producerPalette.length] }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Ürün" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif Ürün" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Ürün Grubu" value={groupSet.size.toLocaleString('tr-TR')} hint="farklı grup" />
        <KpiCard label="Üretici" value={producerSet.size.toLocaleString('tr-TR')} hint="farklı üretici" />
        <KpiCard label="Görselli Ürün" value={`%${total > 0 ? ((withImage / total) * 100).toFixed(0) : 0}`} hint={`${withImage} / ${total}`} />
        <KpiCard label="Bu Ay Eklenen" value={newThisMonth.toLocaleString('tr-TR')} hint="Yeni ürün" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Ürün Grubuna Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {groupData.length} grup</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={groupData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} ürün`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} ürün`} data={statusData} />
        <DonutCard title="Üreticiye Göre" subtitle={`İlk ${producerData.length}`} data={producerData} />
      </div>
    </div>
  );
}

// detailBase: ürün kartının rota öneki. Varsayılan /products. "Ürün V2" prototipi
// (/products-v2) aynı listeyi kullanıp kartı V2 ekranına yönlendirmek için verir.
export function ProductsPage({ detailBase = '/products' } = {}) {
  usePageMeta('Ürünler', 'Ürün kayıtlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [activeDefinition, setActiveDefinition] = useState('products');

  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [producers, setProducers] = useState([]);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(DEFAULT_HIDDEN_COLUMNS);

  // $select: düz liste görünümünde yalnız GÖRÜNÜR kolonların OData kaynak alanlarını çekeriz
  // (Column Chooser ile entegre). COLUMN_FILTER_META meta'sı olan kolonlar görünürlüğe göre
  // eklenir; `always` = 'Id' (satır anahtarı + /products/:id nav) + Ürün Kartı (v2) ek kolonları
  // (COLUMN_FILTER_META'da odataField'leri YOK, aksi halde ColumnChooser'dan açılınca boş gelir).
  // productImage kolonu GÖRÜNÜRSE thumbnail için ProductImage'ı da seçeriz; backend büyük $top'ta
  // ($top>100 → loadMore) yine strip'ler, yani ilk sayfa görsel gelir, loadMore şişmez (orijinal davranış).
  const selectFields = useMemo(() => {
    const fields = buildSelectFields({
      columnFilterMeta: COLUMN_FILTER_META,
      columnVisibility,
      always: [
        'Id', 'Name', // Name: silme onay diyaloğu selected.name okur (Adı kolonu gizlense de gerekir)
        'TrackingCode', 'Prefix', 'ApplicationArea', 'ApprovalStatus', 'ProductType', 'Gtip',
        'CenterCode', 'SpecialCode', 'CountryCode', 'AlcoholRate', 'MaxDiscountRate',
        'MaxDiscountAmount', 'Weight', 'Volume', 'Liter', 'CaseQuantity',
        'Level1ProductId', 'Level2ProductId', 'DynamicGroupId',
      ],
    });
    if (fields && columnVisibility.productImage !== false) fields.push('ProductImage');
    return fields;
  }, [columnVisibility]);
  const [importOpen, setImportOpen] = useState(false);
  // Toplu liste/kanban çekiminde productImage backend'de strip edildiği için, düzenlemede
  // görsel değiştirilmediyse kaydederken mevcut görseli koru (yoksa save görseli siler).
  const [imageDirty, setImageDirty] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getGroupLabel = useCallback((id) => {
    const g = groups.find((item) => item.id === id);
    return g?.description || g?.name || g?.textId || '-';
  }, [groups]);
  const getSubGroupLabel = useCallback((id) => {
    const g = subGroups.find((item) => item.id === id);
    return g?.description || g?.name || g?.textId || '-';
  }, [subGroups]);
  const getProducerLabel = useCallback((id) => producers.find((item) => item.id === id)?.name || '-', [producers]);

  const loadLookups = useCallback(() => {
    const headers = authHeaders();
    return Promise.all([
      fetch('/api/productgroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/productsubgroups/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/producers/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/productadditionalfields/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([groupData, subGroupData, producerData, fieldData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
      setProducers(unwrapLookupItems(producerData));
      setAdditionalFields(unwrapLookupItems(fieldData));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  // İçe aktarımda satırlar arası paylaşılan grup/ek grup/marka çözümleme önbelleği
  // (her import açılışında sıfırlanır; aynı isim/kod için tek kez kayıt oluşturulur).
  const importRelCacheRef = useRef(null);

  // Gruplama etiket çözücü (hook'a verilir)
  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = field === 'status' ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
    else if (field === 'groupId') label = getGroupLabel(key);
    else if (field === 'subGroupId') label = getSubGroupLabel(key);
    else if (field === 'producerId') label = getProducerLabel(key);
    else label = String(key);
    return { key, label };
  }, [getGroupLabel, getSubGroupLabel, getProducerLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'status') return Object.entries(STATUS_META).map(([k, meta]) => ({ key: Number(k), label: meta.label }));
    if (groupField === 'groupId') return groups.map((g) => ({ key: g.id, label: getGroupLabel(g.id) }));
    if (groupField === 'subGroupId') return subGroups.map((g) => ({ key: g.id, label: getSubGroupLabel(g.id) }));
    if (groupField === 'producerId') return producers.map((p) => ({ key: p.id, label: getProducerLabel(p.id) }));
    return [];
  }, [groups, subGroups, producers, getGroupLabel, getSubGroupLabel, getProducerLabel]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    defaultKanbanGroup: 'status',
    getKeyLabel,
    seedKanbanBuckets,
    selectFields,
    ready: lookupsLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  // === Form / CRUD ===
  // Tek ürünün tam kaydını (görsel dahil) GetById ile getir — liste/kanban satırlarında
  // productImage strip edilmiş olabilir.
  const fetchProductImage = useCallback(async (id) => {
    if (!id) return '';
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      if (!res.ok) return '';
      const body = await res.json().catch(() => null);
      const item = body?.data ?? body?.Data ?? body;
      return item?.productImage ?? item?.ProductImage ?? '';
    } catch {
      return '';
    }
  }, []);

  // Görüntüle/Düzenle/Yeni artık ayrı detay sayfasına (/products/:id) gider.
  const openNew = () => navigate(`${detailBase}/new`);
  const openEdit = (row = selected) => { if (!row) return; navigate(`${detailBase}/${row.id}?edit=1`); };
  const openView = (row = selected) => { if (!row) return; navigate(`${detailBase}/${row.id}`); };
  const closeModal = () => setModal(null);

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { showToast('error', "Görsel 2 MB'dan küçük olmalı"); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((current) => ({ ...current, productImage: reader.result })); setImageDirty(true); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.textId.trim()) { showToast('error', 'Ürün kodu boş olamaz'); return; }
    if (!form.name.trim()) { showToast('error', 'Ürün adı boş olamaz'); return; }
    const missingRequired = additionalFields.find((f) => f.isRequired && !String(fieldValues[f.id] ?? '').trim());
    if (missingRequired) { showToast('error', `"${missingRequired.name}" alanı zorunludur`); return; }

    const productId = modal === 'new'
      ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : undefined)
      : selected.id;

    // Görsel koruma: düzenlemede görsel değiştirilmediyse (imageDirty=false), liste/kanban
    // satırında strip edilmiş olabileceği için mevcut görseli koru — aksi halde save siler.
    let productImage;
    if (imageDirty || modal === 'new') {
      productImage = toRawBase64(form.productImage);
    } else if (form.productImage) {
      productImage = toRawBase64(form.productImage);
    } else {
      productImage = (await fetchProductImage(selected.id)) || null;
    }

    const payload = {
      textId: form.textId, name: form.name,
      shortName: form.shortName || null, description: form.description || null,
      erpId: form.erpId || null, status: toRequiredInt(form.status), unit: toRequiredInt(form.unit),
      groupId: toNullableGuid(form.groupId), subGroupId: toNullableGuid(form.subGroupId),
      producerId: toNullableGuid(form.producerId), productImage,
      // Faz 0: Update artık attach-all ile DefaultStockType'ı da yazar (backend'deki geçici koruma
      // kaldırıldı) → bu hızlı formdan kaydederken mevcut değeri EKO'la, aksi halde sıfırlanır.
      defaultStockType: Number(selected?.defaultStockType ?? 0),
    };
    const saveFieldValues = async (pid) => {
      if (!pid || additionalFields.length === 0) return;
      await fetch('/api/productadditionalfields/SaveValues', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ productId: pid, values: additionalFields.map((f) => ({ additionalFieldId: f.id, value: String(fieldValues[f.id] ?? '') })) }),
      });
    };
    try {
      if (modal === 'new') {
        const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...payload, id: productId }) });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt eklenemedi');
        await saveFieldValues(productId);
        showToast('success', 'Ürün başarıyla eklendi');
      } else {
        const res = await fetch(`${API_BASE}/Update`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id, ...payload }) });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt güncellenemedi');
        await saveFieldValues(selected.id);
        showToast('success', 'Ürün başarıyla güncellendi');
      }
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Ürün başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  // === Kolonlar ===
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Ürün Kodu', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', {
      header: 'Adı', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        return <span className="c360-customer-cell"><AvatarBadge name={v} tone="customer" /><span className="c360-cell-main">{v}</span></span>;
      },
    }),
    columnHelper.accessor('shortName', { header: 'Kısa Adı', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('unit', { header: 'Birim', meta: { filterType: 'number' }, cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('productImage', {
      id: 'productImage', header: 'Görsel', enableSorting: false,
      cell: (info) => <ProductThumb value={info.getValue()} />,
    }),
    columnHelper.accessor((row) => getGroupLabel(row.groupId), {
      id: 'groupLabel', header: 'Ürün Grubu', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor((row) => getSubGroupLabel(row.subGroupId), {
      id: 'subGroupLabel', header: 'Ek Grubu', enableSorting: false, meta: { filterType: 'select' },
    }),
    columnHelper.accessor((row) => getProducerLabel(row.producerId), {
      id: 'producerLabel', header: 'Üretici', enableSorting: false, meta: { filterType: 'select' },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined || v === '') return '-';
        const meta = STATUS_META[v] ?? { label: String(v), tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    // #717 "Varsayılan Stok Tipi" kolonu kaldırıldı (alan komple söküldü).
    // Ürün Kartı (v2) ek alanları — Kolonlar menüsünde varsayılan kapalı (tiksiz).
    columnHelper.accessor('trackingCode', { header: 'Takip Kodu', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('prefix', { header: 'Ön Ek', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('applicationArea', { header: 'Uygulama Yeri', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('approvalStatus', { header: 'Onay', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('productType', { header: 'Ürün Tipi', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('gtip', { header: 'GTIP', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('centerCode', { header: 'Merkez Kod', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('specialCode', { header: 'Özel Kod', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('countryCode', { header: 'Ülke Kodu', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('alcoholRate', { header: 'Alkol %', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('maxDiscountRate', { header: 'Maks. İsk. %', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('maxDiscountAmount', { header: 'Maks. İsk. Tutar', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('weight', { header: 'Ağırlık', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('volume', { header: 'Hacim', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('liter', { header: 'Litre', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('caseQuantity', { header: 'Koli İçi', cell: (info) => info.getValue() ?? '-' }),
    columnHelper.accessor('level1ProductId', { header: 'Seviyeli Ürün-1', enableSorting: false, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('level2ProductId', { header: 'Seviyeli Ürün-2', enableSorting: false, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('dynamicGroupId', { header: 'Dinamik Grup', enableSorting: false, cell: (info) => info.getValue() || '-' }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getGroupLabel, getSubGroupLabel, getProducerLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    groupLabel: groups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    subGroupLabel: subGroups.map((g) => ({ value: g.id, label: g.description ? `${g.textId} - ${g.description}` : (g.textId || g.id) })),
    producerLabel: producers.map((p) => ({ value: p.id, label: p.name ? `${p.textId || ''} - ${p.name}`.replace(/^- /, '') : (p.textId || p.id) })),
  }), [groups, subGroups, producers]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options || lookupOptions[columnId] }), [lookupOptions]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;
  const inputField = (label, key, type = 'text') => (
    <div style={s.formGroup}>
      <label style={s.label}>{label}</label>
      <input style={inputStyle} type={type} value={form[key]} onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.value }))} readOnly={isView} />
    </div>
  );

  // === Export / Import ===
  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Ürün Kodu' },
        { field: 'name', headerName: 'Adı' },
        { field: 'shortName', headerName: 'Kısa Adı' },
        { field: 'unit', headerName: 'Birim' },
        { field: 'erpId', headerName: 'ERP Kodu' },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
        { headerName: 'Ürün Grubu', valueGetter: ({ row }) => getGroupLabel(row.groupId) },
        { headerName: 'Ek Grubu', valueGetter: ({ row }) => getSubGroupLabel(row.subGroupId) },
        { headerName: 'Üretici', valueGetter: ({ row }) => getProducerLabel(row.producerId) },
        { field: 'description', headerName: 'Açıklama' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `urunler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const importColumnKeys = {
    textId: ['Stok Kodu', 'Ürün Kodu', 'Urun Kodu', 'textId', 'TextId', 'Kod'],
    name: ['Stok Adı', 'Stok Adi', 'Ürün Adı', 'Urun Adi', 'Adı', 'Adi', 'Ad', 'name', 'Name'],
    shortName: ['Kısa Adı', 'Kisa Adi', 'shortName', 'ShortName'],
    erpId: ['ERP Kodu', 'erpId', 'ErpId'],
    unit: ['Birim', 'unit', 'Unit'],
    // Faz 0 — Varsayılan Stok Tipi (Satılabilir/Bedelsiz/Depozito/Hasarlı ya da 0..3). Boş = Satılabilir.
    stockType: ['Varsayılan Stok Tipi', 'Varsayilan Stok Tipi', 'Stok Tipi', 'stockType', 'DefaultStockType'],
    groupCode: ['Grup Kodu', 'Grup Kod', 'groupCode', 'GroupCode'],
    groupName: ['Grup Adı', 'Grup Adi', 'Grup', 'groupName', 'GroupName', 'Group'],
    subGroupCode: ['Ek Grup Kodu', 'Alt Grup Kodu', 'subGroupCode', 'SubGroupCode'],
    subGroupName: ['Ek Grup Adı', 'Ek Grup Adi', 'Alt Grup Adı', 'Ek Grup', 'subGroupName', 'SubGroupName'],
    producerName: ['Üretici', 'Uretici', 'Marka', 'producerName', 'Producer', 'Brand'],
    // #713 Ek ürün nitelikleri — şablonda yer alsın ve import'ta okunup kaydedilsin.
    productType: ['Ürün Tipi', 'Urun Tipi', 'Tip', 'productType', 'ProductType'],
    salesVatPercentage: ['Satış KDV Oranı', 'Satis KDV Orani', 'Satış KDV', 'Satis KDV', 'salesVatPercentage', 'SalesVatPercentage'],
    purchaseVatPercentage: ['Alış KDV Oranı', 'Alis KDV Orani', 'Alış KDV', 'Alis KDV', 'purchaseVatPercentage', 'PurchaseVatPercentage'],
    weight: ['Ağırlık', 'Agirlik', 'Ağırlık (kg)', 'weight', 'Weight'],
    volume: ['Hacim', 'volume', 'Volume'],
    liter: ['Litre', 'liter', 'Liter'],
    alcoholRate: ['Alkol Oranı', 'Alkol Orani', 'alcoholRate', 'AlcoholRate'],
    gtip: ['GTIP', 'GTIP No', 'Gtip', 'gtip'],
  };
  // #811 ProductUnit (birim) import kolonları. Birim 1 = baz birim (çarpanı HEP 1); Birim 2..5 için
  // Çevrim 2..5 ana birime göre çarpan. Not: bağlanılacak bir `Unit` lookup tablosu YOK — birim adı
  // serbest metindir (ürün kartındaki ProductUnit ile aynı), o yüzden "lookup eşleşmezse hata" kuralı
  // uygulanmaz. Eski tek-'Birim' (int) şablonuyla geriye uyumluluk için 'Birim' aliası ayrı tutulur.
  const UNIT_SLOTS = [
    { nameKeys: ['Birim 1', 'Birim1'], multKeys: null },
    { nameKeys: ['Birim 2', 'Birim2'], multKeys: ['Çevrim 2', 'Cevrim 2', 'Çevrim2', 'Cevrim2'] },
    { nameKeys: ['Birim 3', 'Birim3'], multKeys: ['Çevrim 3', 'Cevrim 3', 'Çevrim3', 'Cevrim3'] },
    { nameKeys: ['Birim 4', 'Birim4'], multKeys: ['Çevrim 4', 'Cevrim 4', 'Çevrim4', 'Cevrim4'] },
    { nameKeys: ['Birim 5', 'Birim5'], multKeys: ['Çevrim 5', 'Cevrim 5', 'Çevrim5', 'Cevrim5'] },
  ];
  const importRowToPayload = (row) => {
    const pick = (keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
    const out = {};
    for (const [field, keys] of Object.entries(importColumnKeys)) out[field] = pick(keys);
    // #811 Birim/çevrim kolonlarını oku. hasUnitColumns: şablonda bu başlıklar var mı (eski şablon değilse).
    const unitHeaderKeys = UNIT_SLOTS.flatMap((sl) => [...sl.nameKeys, ...(sl.multKeys || [])]);
    out.hasUnitColumns = unitHeaderKeys.some((k) => k in row);
    out.unitRows = UNIT_SLOTS.map((sl, i) => ({
      name: pick(sl.nameKeys),
      mult: i === 0 ? '1' : pick(sl.multKeys), // Birinci birimin çarpanı hep 1
    }));
    return out;
  };
  const importValidateRow = (p) => {
    if (!p.textId) return 'Stok Kodu boş';
    if (!p.name) return 'Stok Adı boş';
    if (p.stockType && stockTypeFromLabel(p.stockType) === null) {
      return `Geçersiz Stok Tipi: ${p.stockType} (Satılabilir / Bedelsiz / Depozito / Hasarlı)`;
    }
    // #811 Birim doğrulaması — yalnız şablonda birim kolonları varsa (eski tek-'Birim' şablonuyla uyum).
    if (p.hasUnitColumns) {
      if (!p.unitRows[0].name) return 'Birim 1 (baz birim) boş';
      for (let i = 1; i < p.unitRows.length; i += 1) {
        const u = p.unitRows[i];
        if (!u.name) continue; // boş birim kolonu atlanır
        const m = Number(String(u.mult).replace(',', '.'));
        if (!Number.isFinite(m) || m <= 0) return `Birim ${i + 1} çevrim değeri geçersiz (pozitif sayı olmalı)`;
      }
    }
    return null;
  };

  // --- İlişki (Grup / Ek Grup / Marka) çözümleme: yoksa oluştur, varsa eşle ---
  const REL_IMPORT_META = {
    group: { base: '/api/productgroups', label: 'Grup', nameOf: (it) => it?.description || it?.name, makeBody: (textId, name) => ({ textId, description: name }) },
    subGroup: { base: '/api/productsubgroups', label: 'Ek Grup', nameOf: (it) => it?.description || it?.name, makeBody: (textId, name) => ({ textId, description: name }) },
    producer: { base: '/api/producers', label: 'Üretici', nameOf: (it) => it?.name, makeBody: (textId, name) => ({ textId, name }) },
  };
  const normRel = (v) => String(v ?? '').trim().toLocaleLowerCase('tr');
  const genRelCode = (name) => {
    const base = String(name || '').toLocaleUpperCase('tr')
      .replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G')
      .replace(/Ü/g, 'U').replace(/Ö/g, 'O').replace(/Ç/g, 'C')
      .replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
    return base || 'URETICI';
  };
  const buildImportRelCache = () => {
    const mk = (items, getName) => {
      const byCode = new Map();
      const byName = new Map();
      const usedCodes = new Set();
      (items || []).forEach((it) => {
        if (it?.textId) { byCode.set(normRel(it.textId), it.id); usedCodes.add(normRel(it.textId)); }
        const nm = getName(it);
        if (nm) byName.set(normRel(nm), it.id);
      });
      return { byCode, byName, usedCodes };
    };
    return {
      group: mk(groups, (it) => it.description || it.name),
      subGroup: mk(subGroups, (it) => it.description || it.name),
      producer: mk(producers, (it) => it.name),
    };
  };
  const resolveImportRelation = async (cache, type, rawCode, rawName) => {
    const code = String(rawCode ?? '').trim();
    const name = String(rawName ?? '').trim();
    if (!code && !name) return null;
    const c = cache[type];
    if (code && c.byCode.has(normRel(code))) return c.byCode.get(normRel(code));
    if (name && c.byName.has(normRel(name))) return c.byName.get(normRel(name));
    // Yeni kayıt — Excel'deki kodu kullan, yoksa isimden üret; import içinde tekilleştir
    const meta = REL_IMPORT_META[type];
    let textId = code || genRelCode(name);
    if (c.usedCodes.has(normRel(textId))) {
      let n = 2;
      while (c.usedCodes.has(normRel(`${textId}_${n}`))) n += 1;
      textId = `${textId}_${n}`;
    }
    const res = await fetch(`${meta.base}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(meta.makeBody(textId, name || code)) });
    if (!res.ok) throw new Error(`${meta.label} "${name || code}" oluşturulamadı (${res.status})`);
    // Sunucu Id üretiyor → geri çekip eşle
    const listRes = await fetch(`${meta.base}/Select`, { headers: authHeaders() });
    const items = unwrapLookupItems(listRes.ok ? await listRes.json() : []);
    const found = items.find((it) => normRel(it.textId) === normRel(textId));
    if (!found) throw new Error(`${meta.label} "${name || code}" oluşturuldu ama bulunamadı`);
    c.byCode.set(normRel(textId), found.id);
    if (name) c.byName.set(normRel(name), found.id);
    c.usedCodes.add(normRel(textId));
    return found.id;
  };

  const importSubmitRow = async (p) => {
    if (!importRelCacheRef.current) importRelCacheRef.current = buildImportRelCache();
    const cache = importRelCacheRef.current;
    let groupId = null;
    let subGroupId = null;
    let producerId = null;
    try {
      groupId = await resolveImportRelation(cache, 'group', p.groupCode, p.groupName);
      subGroupId = await resolveImportRelation(cache, 'subGroup', p.subGroupCode, p.subGroupName);
      producerId = await resolveImportRelation(cache, 'producer', '', p.producerName);
    } catch (e) {
      return { ok: false, error: e.message || 'İlişki oluşturulamadı' };
    }
    // #811 ProductUnit'i (SaveForProduct) sonradan bağlamak için ürün Id'sini garanti üret
    // (crypto yoksa manuel v4) — böylece server'ın ürettiği Id'yi bilmeme sorunu olmaz.
    const genId = () => (window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
      }));
    const id = genId();
    const unitVal = p.unit ? toRequiredInt(p.unit) : 1;
    // #713 Türkçe ondalık (virgül) toleranslı sayı parse'ı; boş → null.
    const numOrNull = (v) => { if (v == null || v === '') return null; const n = Number(String(v).replace(',', '.')); return Number.isFinite(n) ? n : null; };
    const payload = {
      id, textId: p.textId, name: p.name, shortName: p.shortName || null, erpId: p.erpId || null,
      // #811 Ürün kartındaki text/varsayılan Birim (Product.Unit int) geriye dönük dolar: baz birim = 1.
      status: 1, unit: unitVal || 1, groupId, subGroupId, producerId, productImage: null,
      // #713 Ek nitelikler (ProductDetailPage kaydıyla aynı alan adları). KDV boş → 0; diğerleri boş → null.
      productType: p.productType || null,
      salesVatPercentage: numOrNull(p.salesVatPercentage) ?? 0,
      purchaseVatPercentage: numOrNull(p.purchaseVatPercentage) ?? 0,
      weight: numOrNull(p.weight), volume: numOrNull(p.volume), liter: numOrNull(p.liter),
      alcoholRate: numOrNull(p.alcoholRate), gtip: p.gtip || null,
      // Faz 0 — katalog stok tipi; boş/eşleşmeyen → 0 (Satılabilir, enum varsayılanı).
      defaultStockType: stockTypeFromLabel(p.stockType) ?? 0,
    };
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
    // #811 Dolu birimlerden ProductUnit kayıtları. Ürün+birim adı anahtarıyla tekilleştir (ilk kazanır);
    // ilk birim = baz (çarpan 1, IsBase). SaveForProduct replace-all olduğu için re-import'ta mükerrer olmaz.
    const filledUnits = (p.unitRows || []).filter((u) => u.name);
    if (filledUnits.length) {
      const seen = new Set();
      const units = [];
      filledUnits.forEach((u) => {
        const key = u.name.toLocaleLowerCase('tr');
        if (seen.has(key)) return;
        seen.add(key);
        const isBase = units.length === 0;
        units.push({
          unitName: u.name,
          multiplier: isBase ? 1 : Number(String(u.mult).replace(',', '.')),
          barcode: null, maxWeight: null, isBase, sortOrder: units.length,
        });
      });
      try {
        const uRes = await fetch('/api/productunits/SaveForProduct', {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ productId: id, units }),
        });
        if (!uRes.ok) return { ok: false, error: `Ürün eklendi ama birimler kaydedilemedi (Sunucu ${uRes.status})` };
      } catch (e) {
        return { ok: false, error: `Ürün eklendi ama birimler kaydedilemedi: ${e.message || 'Network hatası'}` };
      }
    }
    return { ok: true };
  };
  const downloadImportTemplate = () => {
    // #811 Örnek satır statik sabitlerle (başka tenant verisi sızmaz). Birim 1 = baz (çarpan hep 1),
    // Birim 2..5 için Çevrim 2..5 ana birime göre çarpan. Kullanılmayan birimler boş bırakılır.
    const sample = [{
      'Stok Kodu': 'P001', 'Stok Adı': 'Örnek Ürün', 'Kısa Adı': 'Örnek',
      'Grup Kodu': '001', 'Grup Adı': 'Genel Grup',
      'Ek Grup Kodu': '001-1', 'Ek Grup Adı': 'Genel Ek Grup',
      'Marka': 'Örnek Marka',
      // Faz 0 — boş bırakılırsa "Satılabilir" kabul edilir.
      'Varsayılan Stok Tipi': 'Satılabilir',
      // #713 Ek ürün nitelikleri (opsiyonel). Boş bırakılabilir; KDV boşsa 0 kabul edilir.
      'Ürün Tipi': 'Normal', 'Satış KDV Oranı': '20', 'Alış KDV Oranı': '20',
      'Ağırlık': '', 'Hacim': '', 'Litre': '', 'Alkol Oranı': '', 'GTIP': '',
      'Birim 1': 'Adet', 'Birim 2': 'Koli', 'Çevrim 2': '12',
      'Birim 3': '', 'Çevrim 3': '', 'Birim 4': '', 'Çevrim 4': '', 'Birim 5': '', 'Çevrim 5': '',
    }];
    exportToExcel(sample, null, 'urun_sablon');
  };

  const renderDefinitionTabs = () => (
    <div style={productDefinitionStyles.tabBar}>
      {definitionTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          style={{ ...productDefinitionStyles.tab, ...(activeDefinition === tab.key ? productDefinitionStyles.tabActive : {}) }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'groups') return <div style={s.container}>{renderDefinitionTabs()}<ProductGroupsPage /></div>;
  if (activeDefinition === 'subGroups') return <div style={s.container}>{renderDefinitionTabs()}<ProductSubGroupsPage /></div>;
  if (activeDefinition === 'producers') return <div style={s.container}>{renderDefinitionTabs()}<ProducersPage /></div>;

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, ad veya kısa ad ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => { importRelCacheRef.current = null; setImportOpen(true); }}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      {renderDefinitionTabs()}
      <ListPageShell
        title="Ürünler"
        subtitle="Ürün kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={PRODUCT_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Ürün</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getGroupLabel={getGroupLabel} getProducerLabel={getProducerLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const rowStatusMeta = STATUS_META[row.status];
                        const isV = (id) => columnVisibility[id] !== false;
                        const groupLbl = getGroupLabel(row.groupId);
                        const producerLbl = getProducerLabel(row.producerId);
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openView(row)}>
                            {isV('name') && (
                              <div className="c360-kanban-card-head">
                                {row.name && <AvatarBadge name={row.name} tone="customer" size={28} />}
                                <span className="c360-kanban-card-title" title={row.name || ''}>{row.name || '-'}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              {isV('textId') && row.textId && <span className="c360-erp-chip">{row.textId}</span>}
                              {isV('status') && rowStatusMeta && <StatusBadge tone={rowStatusMeta.tone}>{rowStatusMeta.label}</StatusBadge>}
                              {isV('groupLabel') && groupLbl && groupLbl !== '-' && <span className="c360-erp-chip" title="Ürün Grubu">{groupLbl}</span>}
                              {isV('producerLabel') && producerLbl && producerLbl !== '-' && <span className="c360-erp-chip" title="Üretici">{producerLbl}</span>}
                            </div>
                            {isV('shortName') && row.shortName && <div className="c360-kanban-card-sub">{row.shortName}</div>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const meta = STATUS_META[row.status];
              const grp = getGroupLabel(row.groupId);
              const prod = getProducerLabel(row.producerId);
              return {
                avatarName: row.name,
                avatarTone: 'customer',
                title: row.name,
                subtitle: row.textId,
                statusTone: meta?.tone,
                statusLabel: meta?.label,
                meta: [
                  grp && grp !== '-' ? { k: 'Grup', v: grp } : null,
                  prod && prod !== '-' ? { k: 'Üretici', v: prod } : null,
                  row.shortName ? { k: 'Kısa Ad', v: row.shortName } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openView },
              { kind: 'edit', title: 'Değiştir', onClick: openEdit, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openView}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openView}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{modal === 'new' ? 'Yeni Ürün' : modal === 'edit' ? 'Ürün Düzenle' : 'Ürün Detay'}</h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>{inputField('Ürün Kodu *', 'textId')}{inputField('Ürün Adı *', 'name')}</div>
              <div style={s.formRow}>{inputField('Kısa Adı', 'shortName')}{inputField('ERP Kodu', 'erpId')}</div>
              <div style={s.formRow}>{inputField('Birim', 'unit', 'number')}
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  {isView ? <input style={s.inputView} readOnly value={STATUS_META[form.status]?.label ?? form.status ?? ''} />
                    : (
                      <select style={selectStyle} value={form.status} onChange={(e) => setForm((c) => ({ ...c, status: e.target.value }))}>
                        <option value="">Seçiniz</option>
                        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün Grubu</label>
                  {isView ? <input style={s.inputView} readOnly value={getGroupLabel(form.groupId)} />
                    : (
                      <RelationPicker config={RELATION_CONFIGS.productGroup} rows={groups}
                        value={form.groupId} selectedLabel={form.groupId ? getGroupLabel(form.groupId) : ''}
                        onChange={(val) => setForm((c) => ({ ...c, groupId: val }))} />
                    )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ek Grubu</label>
                  {isView ? <input style={s.inputView} readOnly value={getSubGroupLabel(form.subGroupId)} />
                    : (
                      <RelationPicker config={RELATION_CONFIGS.productSubGroup} rows={subGroups}
                        value={form.subGroupId} selectedLabel={form.subGroupId ? getSubGroupLabel(form.subGroupId) : ''}
                        onChange={(val) => setForm((c) => ({ ...c, subGroupId: val }))} />
                    )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Üretici</label>
                  {isView ? <input style={s.inputView} readOnly value={getProducerLabel(form.producerId)} />
                    : (
                      <RelationPicker config={RELATION_CONFIGS.producer} rows={producers}
                        value={form.producerId} selectedLabel={form.producerId ? getProducerLabel(form.producerId) : ''}
                        onChange={(val) => setForm((c) => ({ ...c, producerId: val }))} />
                    )}
                </div>
                {inputField('Açıklama', 'description')}
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Ürün Görseli</label>
                <ProductImagePreview value={form.productImage} name={form.name} />
                {!isView && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: form.productImage ? 8 : 0 }}>
                    <input type="file" accept="image/*" onChange={handleImageFile} />
                    {form.productImage && <button type="button" style={s.btnSecondary} onClick={() => { setForm((c) => ({ ...c, productImage: '' })); setImageDirty(true); }}>Görseli kaldır</button>}
                  </div>
                )}
                {isView && !form.productImage && <span style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>Görsel yok</span>}
              </div>

              {additionalFields.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 14 }}>
                  <label style={{ ...s.label, fontSize: 14, marginBottom: 8, display: 'block' }}>Özel Alanlar</label>
                  <div style={s.formRow}>
                    {additionalFields.map((field) => {
                      const val = fieldValues[field.id] ?? '';
                      const setVal = (v) => setFieldValues((current) => ({ ...current, [field.id]: v }));
                      const dt = Number(field.dataType);
                      return (
                        <div style={s.formGroup} key={field.id}>
                          <label style={s.label}>{field.name}{field.isRequired ? ' *' : ''}</label>
                          {dt === 4 ? (
                            isView ? <input style={s.inputView} readOnly value={val === 'true' ? 'Evet' : val === 'false' ? 'Hayır' : ''} />
                              : (
                                <select style={selectStyle} value={val} onChange={(e) => setVal(e.target.value)}>
                                  <option value="">Seçiniz</option>
                                  <option value="true">Evet</option>
                                  <option value="false">Hayır</option>
                                </select>
                              )
                          ) : (
                            <input style={inputStyle} type={dt === 2 ? 'number' : dt === 3 ? 'date' : 'text'} value={val} onChange={(e) => setVal(e.target.value)} readOnly={isView} placeholder={isView ? '' : `${field.name} giriniz`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'İptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> ürününü silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { importRelCacheRef.current = null; if (okCount > 0) { showToast('success', `${okCount} ürün içe aktarıldı`); loadLookups(); fetchData(); } }}
        title="Ürün İçe Aktar"
        helpText="Şablondaki kolon başlıklarını koruyun. Zorunlu alanlar: Stok Kodu ve Stok Adı. Grup, Ek Grup ve Üretici (kolon başlığı 'Üretici' veya 'Marka' olabilir) otomatik eşlenir; sistemde yoksa yeni kayıt olarak oluşturulur. 'Varsayılan Stok Tipi' opsiyoneldir (Satılabilir / Bedelsiz / Depozito / Hasarlı) — boş bırakılırsa Satılabilir kabul edilir."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
